import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Button, Space, Alert, Tabs, Tag, DatePicker, Row, Col, Modal } from 'antd';
import {
  DashboardOutlined,
  AreaChartOutlined,
  TeamOutlined,
  UserOutlined,
  DollarOutlined,
  ShoppingOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  CalendarOutlined,
  AppstoreOutlined,
  TrophyOutlined,
  SyncOutlined,
  StopOutlined,
  LineChartOutlined,
  ShoppingCartOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import LayoutPage from '../layouts/Main';
import DashboardService from '../utils/api/DashboardService';
import FinancialDashboardService from '../utils/api/financial/FinancialDashboardService';
import OrderListAnalytics from './Dashboard/OrderListAnalytics';
import ClientListAnalytics from './Dashboard/ClientListAnalytics';
import moment from 'moment';
import './Dashboard/dashboard.css';
import DashboardSkeleton from './Dashboard/DashboardSkeleton';
import MarketplaceAnalytics from './Dashboard/MarketplaceAnalytics';
import MarketplaceProductsAnalysis from './Dashboard/MarketplaceProductsAnalysis';
import OrderAnalysis from './Dashboard/OrderAnalysis';
import KPICard from './Dashboard/KPICard';
import SalesMetrics from './Dashboard/SalesMetrics';
import ServiceMetrics from './Dashboard/ServiceMetrics';
import UserMetrics from './Dashboard/UserMetrics';
import TopClients from './Dashboard/TopClients';
import SalesTrends from './Dashboard/SalesTrends';
import DollarRateCard from './Dashboard/DollarRateCard';
import FinancialIndicators from './Dashboard/FinancialIndicators';
import FinancialSummary from './Dashboard/FinancialSummary';
import SalesProgress from './Dashboard/SalesProgress';
import BankAccountIncome from './Dashboard/BankAccountIncome';
import ExpensesReport from './Dashboard/ExpensesReport';
import DashboardPaymentCalendar from './Dashboard/DashboardPaymentCalendar';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const monthsES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const formatDateRange = (startDate, endDate) => {
  const formatDate = (dateString) => {
    const date = moment(dateString, 'YYYY-MM-DD');
    const day = String(date.date()).padStart(2, '0');
    const month = String(date.month() + 1).padStart(2, '0');
    const year = date.year();
    return `${day}/${month}/${year}`;
  };
  return `${formatDate(startDate)} - ${formatDate(endDate)}`;
};

const formatMonthRange = (startMonth, endMonth) => {
  const formatMonth = (dateString) => {
    const date = moment(dateString, 'YYYY-MM');
    const monthName = monthsES[date.month()];
    const year = date.year();
    return `${monthName} ${year}`;
  };
  return `${formatMonth(startMonth)} - ${formatMonth(endMonth)}`;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    sales: {},
    services: {},
    users: {},
    trends: {},
    topClients: {},
    topClientsMXN: {},
    topClientsUSD: {},
    marketplaceProducts: {},
    financial: {}
  });
  const [loading, setLoading] = useState(true);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showClientsModal, setShowClientsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState(null);
  const [monthRange, setMonthRange] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [isDefaultRange, setIsDefaultRange] = useState(true);
  const [currentDateInfo, setCurrentDateInfo] = useState('');
  const isDefaultRangeRef = useRef(true);

  const [financialData, setFinancialData] = useState({
    indicators: {},
    summary: {},
    accounts: {},
    expensesReport: {},
  });
  const [financialLoading, setFinancialLoading] = useState(false);
  const financialLoadedRef = useRef(false);
  const activeTabRef = useRef('overview');
  const currentParamsRef = useRef({});

  const navigateToOrders = (status = null) => {
    const filters = new URLSearchParams();

    if (dateRange && dateRange.length === 2) {
      filters.append('startDate', dateRange[0].format('YYYY-MM-DD'));
      filters.append('endDate', dateRange[1].format('YYYY-MM-DD'));
    }
    else if (monthRange && monthRange.length === 2) {
      filters.append('startDate', monthRange[0].clone().startOf('month').format('YYYY-MM-DD'));
      filters.append('endDate', monthRange[1].clone().endOf('month').format('YYYY-MM-DD'));
    }
    else if (selectedYear !== new Date().getFullYear()) {
      filters.append('startDate', `${selectedYear}-01-01`);
      filters.append('endDate', `${selectedYear}-12-31`);
    }
    else {
      const today = moment();
      const firstDayOfMonth = today.clone().startOf('month');
      filters.append('startDate', firstDayOfMonth.format('YYYY-MM-DD'));
      filters.append('endDate', today.format('YYYY-MM-DD'));
    }

    if (status) {
      filters.append('status', status);
    }

    navigate(`/orders/finished?${filters.toString()}`);
  };

  const loadFinancialData = useCallback(async (params = {}) => {
    try {
      setFinancialLoading(true);
      const data = await FinancialDashboardService.getAllFinancialData(params);
      setFinancialData(data);
      financialLoadedRef.current = true;
    } catch (err) {
      console.error('Error al cargar datos financieros:', err);
    } finally {
      setFinancialLoading(false);
    }
  }, []);

  const loadDashboardData = useCallback(async (params = {}, isCustomRange = false, isMonthRange = false) => {
    try {
      setLoading(true);
      setError(null);

      let requestParams = params;
      let infoText = '';

      const hasParams = Object.keys(params).length > 0;

      if (!hasParams && isDefaultRangeRef.current) {
        const today = moment();
        const firstDayOfMonth = today.clone().startOf('month');

        requestParams = {
          start_date: firstDayOfMonth.format('YYYY-MM-DD'),
          end_date: today.format('YYYY-MM-DD')
        };

        setDateRange([firstDayOfMonth, today]);
        setSelectedYear(today.year());
        setIsDefaultRange(true);
        isDefaultRangeRef.current = true;

        infoText = `${monthsES[today.month()]} ${today.year()}`;

      } else if (params.start_date && params.end_date) {
        setIsDefaultRange(false);
        isDefaultRangeRef.current = false;

        if (isCustomRange) {
          if (isMonthRange) {
            infoText = formatMonthRange(params.start_date, params.end_date);
          } else {
            infoText = formatDateRange(params.start_date, params.end_date);
          }
        } else {
          const start = moment(params.start_date, 'YYYY-MM-DD');
          infoText = `${monthsES[start.month()]} ${start.year()}`;
        }

      } else if (params.year) {
        setIsDefaultRange(false);
        isDefaultRangeRef.current = false;
        infoText = `Año ${params.year}`;
      }

      setCurrentDateInfo(infoText);

      const response = await DashboardService.getAllDashboardData(requestParams);
      
      if (response.success) {
        const data = response.data || response;

        setDashboardData({
          sales: data.sales || data.financial || {},
          services: data.services || data.operational || {},
          users: data.users || {},
          trends: data.trends || {},
          topClients: data.topClients || {},
          topClientsMXN: data.topClientsMXN || {},
          topClientsUSD: data.topClientsUSD || {},
          marketplaceProducts: data.marketplaceProducts || {},
          financial: data.financial || {}
        });

        setLastUpdated(new Date().toLocaleTimeString());

        currentParamsRef.current = requestParams;
        if (activeTabRef.current === 'financial') {
          loadFinancialData(requestParams);
        } else {
          financialLoadedRef.current = false;
        }
      } else {
        setError(response.message || 'Error en la respuesta del servidor');
      }

    } catch (error) {
      console.error("Error al cargar dashboard:", error);
      setError('Error al cargar los datos del dashboard. Verifique la conexión o intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [loadFinancialData]);

  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    loadDashboardData({
      start_date: firstDayOfMonth.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0]
    });
  }, [loadDashboardData]);

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setMonthRange(null);

    if (dates && dates.length === 2) {
      const [start, end] = dates;

      loadDashboardData({
        start_date: start.format('YYYY-MM-DD'),
        end_date: end.format('YYYY-MM-DD')
      }, true, false);
    } else {
      const today = moment();
      const firstDayOfMonth = today.clone().startOf('month');

      loadDashboardData({
        start_date: firstDayOfMonth.format('YYYY-MM-DD'),
        end_date: today.format('YYYY-MM-DD')
      }, false, false);
    }
  };

  const handleMonthRangeChange = (dates) => {
    setMonthRange(dates);
    setDateRange(null);

    if (dates && dates.length === 2) {
      const [start, end] = dates;

      const startDate = start.clone().startOf('month').format('YYYY-MM-DD');
      const endDate = end.clone().endOf('month').format('YYYY-MM-DD');

      loadDashboardData({
        start_date: startDate,
        end_date: endDate
      }, true, true);
    } else {
      const today = moment();
      const firstDayOfMonth = today.clone().startOf('month');

      loadDashboardData({
        start_date: firstDayOfMonth.format('YYYY-MM-DD'),
        end_date: today.format('YYYY-MM-DD')
      }, false, false);
    }
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setDateRange(null);
    setMonthRange(null);

    if (year === new Date().getFullYear()) {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      loadDashboardData({
        start_date: firstDayOfMonth.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      }, false, false);
    } else {
      loadDashboardData({ year: year }, false, false);
    }
  };

  const handleRefresh = () => {
    if (dateRange && dateRange.length === 2) {
      loadDashboardData({
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD')
      }, true, false);
    } else if (monthRange && monthRange.length === 2) {
      const startDate = monthRange[0].clone().startOf('month').format('YYYY-MM-DD');
      const endDate = monthRange[1].clone().endOf('month').format('YYYY-MM-DD');

      loadDashboardData({
        start_date: startDate,
        end_date: endDate
      }, true, true);
    } else if (selectedYear !== new Date().getFullYear()) {
      loadDashboardData({ year: selectedYear }, false, false);
    } else {
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

      loadDashboardData({
        start_date: firstDayOfMonth.toISOString().split('T')[0],
        end_date: today.toISOString().split('T')[0]
      }, false, false);
    }
  };

  const handleResetFilters = () => {
    setDateRange(null);
    setMonthRange(null);
    setSelectedYear(new Date().getFullYear());
    setIsDefaultRange(true);
    isDefaultRangeRef.current = true;

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    loadDashboardData({
      start_date: firstDayOfMonth.toISOString().split('T')[0],
      end_date: today.toISOString().split('T')[0]
    }, false, false);
  };

  const kpis = useMemo(() => {
    const { sales, users, services, marketplaceProducts } = dashboardData;

    const totalServices = (services.services_by_category || []).reduce((sum, cat) => sum + (cat.count || 0), 0);
    const deliveredServices = (services.services_by_status || []).find(s => s.status_code === 7)?.count || 0;

    const activeClients = (users.active_users_by_role || []).find(r => r.role_code === 4)?.count || 0;
    const activeSuppliers = (users.active_users_by_role || []).find(r => r.role_code === 6)?.count || 0;

    const totalProducts = marketplaceProducts.total_products || 0;
    

    return [
      {
        title: 'Ingresos Totales de MXN',
        value: sales.total_sales_mxn,
        icon: <DollarOutlined />,
        color: 'green',
        format: 'currency'
      
      },
      {
        title: 'Ingresos Totales de USD',
        value: sales.total_sales_usd,
        icon: <DollarOutlined />,
        color: 'red',
        format: 'currency'
      
      },
      {
        title: 'Órdenes Totales',
        value: sales.total_orders || 0,
        icon: <ShoppingOutlined />,
        color: 'blue'
      },
      {
        title: 'Clientes Atendidos',
        value: sales.total_clients || 0,
        icon: <UserOutlined />,
        color: 'purple'
      },
      {
        title: 'Servicios Totales',
        value: totalServices,
        icon: <CheckCircleOutlined />,
        color: 'orange'
      },
      {
        title: 'Usuarios Activos',
        value: users.total_active_users || 0,
        icon: <TeamOutlined />,
        color: 'cyan'
      },
      {
        title: 'Total de Productos del Marketplace',
        value: totalProducts,
        icon: <ShoppingCartOutlined />,
        color: 'magenta',
        format: 'number'
      }
    ];
  }, [dashboardData]);

  if (loading && !dashboardData.sales.total_sales) {
    return <DashboardSkeleton />;
  }

  return (
    <LayoutPage>
      <div className="dashboard-main-container">
        <div className="dashboard-header-section">
          <div className="dashboard-header-content">
            <Row justify="space-between" align="middle">
              <Col>
                <Title level={2} className="dashboard-main-title">
                  <DashboardOutlined /> Monitoreo integral del rendimiento
                </Title>
              </Col>
              <Col>
                {lastUpdated && (
                  <div className="dashboard-info">
                    <Tag icon={<ClockCircleOutlined />} color="blue">
                      Actualizado: {lastUpdated}
                    </Tag>
                  </div>
                )}
              </Col>
            </Row>

            <Row gutter={[16, 8]} align="middle" style={{ marginTop: 8 }}>
              <Col>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {isDefaultRange && (
                    <Tag icon={<InfoCircleOutlined />} color="blue" style={{ fontSize: '12px' }}>
                      Datos del mes actual
                    </Tag>
                  )}
                  <Tag icon={<CalendarOutlined />} color="default" style={{ fontSize: '12px' }}>
                    {currentDateInfo}
                  </Tag>
                </div>
              </Col>
              <Col>
                <DollarRateCard compact={true} />
              </Col>
            </Row>

            <div className="dashboard-filters">
              <Tabs
                activeKey={activeTab}
                onChange={(tab) => {
                  setActiveTab(tab);
                  activeTabRef.current = tab;
                  if (tab === 'financial' && !financialLoadedRef.current) {
                    loadFinancialData(currentParamsRef.current);
                  }
                }}
                className="dashboard-tabs"
                size="small"
              >
                <TabPane
                  tab={
                    <span>
                      <AppstoreOutlined />
                      Overview
                    </span>
                  }
                  key="overview"
                />
                <TabPane
                  tab={
                    <span>
                      <DollarOutlined />
                      Financiero
                    </span>
                  }
                  key="financial"
                />
                <TabPane
                  tab={
                    <span>
                      <ShoppingOutlined />
                      Operativo
                    </span>
                  }
                  key="operational"
                />
                <TabPane
                  tab={
                    <span>
                      <TeamOutlined />
                      Usuarios
                    </span>
                  }
                  key="users"
                />
                <TabPane
                  tab={
                    <span>
                      <LineChartOutlined />
                      Analíticas
                    </span>
                  }
                  key="analytics"
                />
              </Tabs>

              <div className="filter-controls">
                <Space direction="horizontal" size="middle">
                  <div className="filter-group">
                    <FilterOutlined className="filter-icon" />
                    <RangePicker
                      value={dateRange}
                      onChange={handleDateRangeChange}
                      format="DD/MM/YYYY"
                      placeholder={['Fecha inicio', 'Fecha fin']}
                      suffixIcon={<CalendarOutlined />}
                      size="small"
                      allowClear={true}
                    />
                  </div>

                  <div className="filter-group">
                    <FilterOutlined className="filter-icon" />
                    <RangePicker
                      value={monthRange}
                      onChange={handleMonthRangeChange}
                      format="MM/YYYY"
                      placeholder={['Mes inicio', 'Mes fin']}
                      picker="month"
                      suffixIcon={<CalendarOutlined />}
                      size="small"
                      allowClear={true}
                    />
                  </div>

                  <Button
                    onClick={handleRefresh}
                    loading={loading}
                    size="small"
                    type="primary"
                    icon={<SyncOutlined />}
                  >
                    Actualizar
                  </Button>

                  <Button
                    onClick={handleResetFilters}
                    size="small"
                    type="default"
                    icon={<StopOutlined />}
                  >
                    Limpiar
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
            className="dashboard-alert"
          />
        )}

        <div className="dashboard-content">
          {activeTab === 'overview' && (
            <>
              <div className="dashboard-section">
                <Title level={3} className="section-title">
                  <DashboardOutlined /> Indicadores Clave (KPIs)
                </Title>
                <div className="kpi-grid">
                  {kpis.map((kpi, index) => (
                    <KPICard
                      key={index}
                      title={kpi.title}
                      value={kpi.value}
                      icon={kpi.icon}
                      color={kpi.color}
                      format={kpi.format}
                      loading={loading}
                      onClick={() => {
                        if (kpi.title === 'Órdenes Totales') {
                          setShowOrdersModal(true);
                        } else if (kpi.title === 'Clientes Atendidos') {
                          setShowClientsModal(true);
                        }
                      }}
                    />
                  ))}
                </div>
              </div>

              <div className="dashboard-section">
                <Title level={3} className="section-title">
                  <AreaChartOutlined /> Métricas Principales
                </Title>
                <div className="dashboard-grid grid-two-cards">
                  <SalesMetrics
                    data={dashboardData.sales}
                    loading={loading}
                    currentDateInfo={currentDateInfo}
                  />
                  <ServiceMetrics
                    data={dashboardData.services}
                    loading={loading}
                    currentDateInfo={currentDateInfo}
                    onServiceCardClick={(status) => navigateToOrders(status)}
                  />
                </div>
              </div>

              <div className="dashboard-section">
                <Title level={3} className="section-title">
                  <TrophyOutlined /> Clientes Destacados
                </Title>
                <div className="dashboard-grid grid-one-card">
                  <TopClients
                    data={dashboardData.topClients}
                    dataMXN={dashboardData.topClientsMXN}
                    dataUSD={dashboardData.topClientsUSD}
                    loading={loading}
                    limit={5}
                    title="Top 5 Clientes"
                    currentDateInfo={currentDateInfo}
                  />
                </div>
              </div>
            </>
          )}

          {activeTab === 'financial' && (
            <>
              <div className="dashboard-section">
                <Title level={3} className="section-title">
                  <DollarOutlined /> Indicadores Financiero
                </Title>
                <FinancialIndicators
                  data={financialData.indicators}
                  loading={financialLoading}
                  currentDateInfo={currentDateInfo}
                />
              </div>

              <div className="dashboard-section">
                <Title level={3} className="section-title">
                  <AreaChartOutlined /> Métricas Principales
                </Title>
                <div className="dashboard-grid grid-two-cards">
                  <SalesMetrics
                    data={dashboardData.sales}
                    loading={loading}
                    currentDateInfo={currentDateInfo}
                    variant="financial"
                  />
                  <DashboardPaymentCalendar />
                </div>
              </div>

              <div className="dashboard-section">
                <Title level={3} className="section-title">
                  <DollarOutlined /> Ingreso Cuentas de Banco
                </Title>
                <BankAccountIncome
                  data={financialData.accounts}
                  loading={financialLoading}
                  currentDateInfo={currentDateInfo}
                  currentParams={currentParamsRef.current}
                />
              </div>

              <div className="dashboard-section">
                <Title level={3} className="section-title">
                  <DollarOutlined /> Reporte de Gastos
                </Title>
                <ExpensesReport
                  data={financialData.expensesReport}
                  loading={financialLoading}
                  currentDateInfo={currentDateInfo}
                />
              </div>
            </>
          )}

          {activeTab === 'operational' && (
            <div className="dashboard-section">
              <Title level={3} className="section-title">
                <ShoppingOutlined /> Análisis Operativo
              </Title>

              <div className="dashboard-section" style={{ marginBottom: 24 }}>
                <ServiceMetrics
                  data={dashboardData.services}
                  loading={loading}
                  currentDateInfo={currentDateInfo}
                  onServiceCardClick={(status) => navigateToOrders(status)}
                />
              </div>
            </div>
          )}

          {activeTab === 'users' && (
            <div className="dashboard-section">
              <Title level={3} className="section-title">
                <TeamOutlined /> Gestión de Usuarios
              </Title>
              <UserMetrics
                data={dashboardData.users}
                loading={loading}
                currentDateInfo={currentDateInfo}
              />
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="dashboard-section">
              <Title level={3} className="section-title">
                <LineChartOutlined /> Dashboard Analítico
              </Title>

              <div className="dashboard-section" style={{ marginBottom: 24 }}>
                <MarketplaceAnalytics
                  salesData={dashboardData.sales}
                  topClientsData={dashboardData.topClients}
                  loading={loading}
                  currentDateInfo={currentDateInfo}
                />
              </div>

              <div className="dashboard-section" style={{ marginBottom: 24 }}>
                <OrderAnalysis
                  servicesData={dashboardData.services}
                  loading={loading}
                  currentDateInfo={currentDateInfo}
                />
              </div>

              <div className="dashboard-section" style={{ marginBottom: 24 }}>
                <MarketplaceProductsAnalysis
                  marketplaceProductsData={dashboardData.marketplaceProducts}
                  loading={loading}
                  currentDateInfo={currentDateInfo}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        title="Listado de Órdenes"
        open={showOrdersModal}
        onCancel={() => setShowOrdersModal(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setShowOrdersModal(false)}>
            Cerrar
          </Button>
        ]}
      >
        <OrderListAnalytics
          dateRange={dateRange}
          monthRange={monthRange}
          year={selectedYear}
        />
      </Modal>

      <Modal
        title="Clientes Atendidos con Detalle de Ventas"
        open={showClientsModal}
        onCancel={() => setShowClientsModal(false)}
        width="90%"
        style={{ maxWidth: '1400px' }}
        footer={[
          <Button key="close" onClick={() => setShowClientsModal(false)}>
            Cerrar
          </Button>
        ]}
        bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}
      >
        <ClientListAnalytics
          dateRange={dateRange}
          monthRange={monthRange}
          year={selectedYear}
        />
      </Modal>
    </LayoutPage>
  );
}